import { type ReactNode, useCallback, useRef, useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui/IconButton';
import { XStack } from '@/components/ui/layout';
import { classList } from '@/utils/cn';
import { saveBlob } from '@/utils/file-io';
import { handleCopyText } from '@/utils/platform';

// LRU cache with size limit to prevent memory leaks
const MAX_CACHE_SIZE = 100;
const RENDER_FAILED = Symbol('mermaid-render-failed');
const mermaidCache = new Map<string, ReactNode | typeof RENDER_FAILED>();

export { RENDER_FAILED, mermaidCache };

export const mermaidCacheKey = (content: string, dyslexicFont: boolean) =>
  `mermaid:${dyslexicFont}:${content}`;

export function getCachedMermaidDiagram(
  content: string,
  dyslexicFont: boolean
): ReactNode | null {
  const cached = mermaidCache.get(mermaidCacheKey(content, dyslexicFont));
  if (!cached || cached === RENDER_FAILED) return null;
  return cached;
}

export const manageCacheSize = () => {
  if (mermaidCache.size > MAX_CACHE_SIZE) {
    const firstKey = mermaidCache.keys().next().value;
    if (firstKey) mermaidCache.delete(firstKey);
  }
};

export function downloadSvg(svgElement: SVGSVGElement, filename: string) {
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgElement);
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  void saveBlob(blob, filename).catch(() => {
    toast.error('Failed to download diagram');
  });
}

interface MermaidDiagramViewProps {
  diagram: ReactNode;
  copyText: string;
  onExpand?: () => void;
  interactive?: boolean;
  className?: string;
}

export const MermaidDiagramView = ({
  diagram,
  copyText,
  onExpand,
  interactive = false,
  className,
}: MermaidDiagramViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await handleCopyText(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [copyText]);

  const handleDownload = useCallback(() => {
    const svg = containerRef.current?.querySelector(
      'svg'
    ) as SVGSVGElement | null;
    if (svg) downloadSvg(svg, 'mermaid-diagram.svg');
  }, []);

  return (
    <div
      className={classList(
        'h-full w-full',
        'motion-opacity-in-0 motion-duration-500',
        'relative',
        'group/mermaid',
        interactive &&
          'cursor-grab bg-[radial-gradient(theme(colors.gray.4)_1px,transparent_1px)] [background-size:16px_16px]',
        className
      )}
    >
      {onExpand && (
        <div
          className={classList(
            'absolute right-3 top-3 z-10',
            'opacity-0 touch:opacity-100 group-hover/mermaid:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]/mermaid:opacity-100',
            'transition-opacity'
          )}
        >
          <IconButton
            size="sm"
            icon={Icons.expand}
            onClick={onExpand}
            tooltip="Expand"
          />
        </div>
      )}
      <XStack
        className={classList(
          'absolute right-3 z-10',
          onExpand ? 'bottom-5' : 'bottom-3',
          'flex gap-2',
          'opacity-0 touch:opacity-100 group-hover/mermaid:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]/mermaid:opacity-100',
          'transition-opacity'
        )}
      >
        <IconButton
          size="sm"
          icon={copied ? Icons.check : Icons.copy}
          onClick={handleCopy}
          tooltip={copied ? 'Copied' : 'Copy'}
        />
        <IconButton
          size="sm"
          icon={Icons.download}
          onClick={handleDownload}
          tooltip="Download SVG"
        />
      </XStack>
      <div ref={containerRef} className="h-full w-full">
        {interactive ? (
          <TransformWrapper initialScale={1}>
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
            >
              {diagram}
            </TransformComponent>
          </TransformWrapper>
        ) : (
          <div className="flex size-full items-center justify-center overflow-hidden p-6 [&_svg]:max-h-full [&_svg]:max-w-full">
            {diagram}
          </div>
        )}
      </div>
    </div>
  );
};
